// src/features/treinos/utils/loadRecommendation.js
//
// Carga recomendada (módulo PURO, testável). Reusa o Epley (utils/epley.js).
//   • COM histórico: progressão sobre a última carga (RPE/conclusão) com teto
//     Epley inverso.
//   • SEM histórico: estimativa por peso corporal × coeficiente × nível × sexo.
//   • Sem dado utilizável: null (o frontend esconde o kg).
//
// Também exporta parseReps(): colapsa range → 1 número, preservando a unidade
// (reps / segundos / "por lado"). O MESMO valor é usado no display E como
// repsAlvo do cálculo (sem divergir).

import { epley1RM, cargaFromE1RM } from "./epley.js";

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTES — no topo, fáceis de tunar
// ─────────────────────────────────────────────────────────────────────────────
export const ROUND_KG = 2.5;
export const PROGRESSION_STEP = 0.025;
export const RPE_READY = 7;

// TODO: usar quando a exercise_library expor PADRÃO DE MOVIMENTO por exercício.
// Hoje a lib só tem muscle_group → caímos em COEF_GRUPO (abaixo).
export const COEF_MOVIMENTO = {
  SUPINO: 0.5,
  DESENVOLVIMENTO: 0.35,
  AGACHAMENTO: 0.75,
  LEVANTAMENTO_TERRA: 0.9,
  REMADA: 0.5,
  ISOLADO: 0.15,
};

// Fallback ATUAL: coeficiente conservador por GRUPO muscular (chave normalizada).
export const COEF_GRUPO = {
  peito: 0.5,
  costas: 0.5,
  pernas: 0.7,
  inferiores: 0.7,
  ombros: 0.35,
  biceps: 0.15,
  triceps: 0.15,
  isolado: 0.15,
  abdomen: 0.1,
  core: 0.1,
  panturrilha: 0.25,
  funcional: 0.2,
  aerobio: 0,
  mobilidade: 0,
};
export const DEFAULT_COEF = 0.2; // grupo desconhecido → conservador

export const MULT_NIVEL = { INICIANTE: 1.0, MODERADO: 1.3, AVANCADO: 1.6 };
export const MULT_SEXO = { M: 1.0, F: 0.65 };

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
function deburrLower(s = "") {
  return String(s).normalize("NFD").replace(/\p{Diacritic}/gu, "").trim().toLowerCase();
}

function roundTo(value, step) {
  if (!Number.isFinite(value) || step <= 0) return value;
  return Math.round(value / step) * step;
}

function normNivel(nivel) {
  const n = deburrLower(nivel);
  if (!n) return "INICIANTE";
  if (n.startsWith("av")) return "AVANCADO";
  if (n.startsWith("mod") || n.startsWith("inter")) return "MODERADO";
  return "INICIANTE";
}

function normSexo(sexo) {
  const s = deburrLower(sexo);
  return s.startsWith("f") ? "F" : "M";
}

/** Resolve o coeficiente do exercício (movimento → fallback grupo → default). */
function resolveCoef(exercicio) {
  // TODO: preferir COEF_MOVIMENTO[exercicio.padrao] quando a lib tiver o campo.
  if (exercicio?.padrao) {
    const c = COEF_MOVIMENTO[deburrLower(exercicio.padrao).toUpperCase()];
    if (c != null) return c;
  }
  const c = COEF_GRUPO[deburrLower(exercicio?.grupo)];
  return c != null ? c : DEFAULT_COEF;
}

/**
 * Colapsa o campo de reps em UM número preservando a unidade.
 *   "8-12" → { value: 8, unitType:"reps", display:"8 reps" }   (limite inferior)
 *   "30s"  → { value: 30, unitType:"seconds", display:"30s" }
 *   "10 reps por lado" → { value:10, unitType:"reps_per_side", display:"10 reps por lado" }
 * Retorna null se não houver número.
 * // TODO: quando a IA emitir 1 número por série, isto vira passthrough.
 */
export function parseReps(reps) {
  const raw = reps == null ? "" : String(reps).trim();
  if (!raw) return null;
  const low = deburrLower(raw);
  const match = raw.match(/\d+(?:[.,]\d+)?/); // 1º número = limite inferior do range
  if (!match) return null;
  const value = Math.round(parseFloat(match[0].replace(",", ".")));

  const isTime = /\d+\s*s\b/.test(low) || /\b(seg|segundos?)\b/.test(low) || /isometr|prancha|tempo/.test(low);
  const perSide = /por lado|cada lado|unilateral|\/lado/.test(low);

  if (isTime) return { value, unitType: "seconds", display: `${value}s` };
  if (perSide) return { value, unitType: "reps_per_side", display: `${value} reps por lado` };
  return { value, unitType: "reps", display: `${value} reps` };
}

// ─────────────────────────────────────────────────────────────────────────────
// API
// ─────────────────────────────────────────────────────────────────────────────
/**
 * @param {{
 *   historicoExercicio?: { ultimaCarga:number, completou:boolean, rpe:(number|null), e1rmMax:number } | null,
 *   repsAlvo?: number,
 *   perfilUsuario?: { pesoCorporal?:number, nivel?:string, sexo?:string },
 *   exercicio?: { grupo?:string, padrao?:string },
 * }} params
 * @returns {number|null} carga recomendada (kg, múltiplo de ROUND_KG) ou null.
 */
export function recomendarCarga({ historicoExercicio, repsAlvo, perfilUsuario, exercicio } = {}) {
  const alvo = Number(repsAlvo);

  // ── COM histórico (top set da última sessão) ──────────────────────────────
  if (historicoExercicio && Number(historicoExercicio.ultimaCarga) > 0) {
    const ultima = Number(historicoExercicio.ultimaCarga);
    const rpe = historicoExercicio.rpe == null ? null : Number(historicoExercicio.rpe);
    const completou = historicoExercicio.completou !== false;

    let base;
    if (!completou || rpe === 10) base = ultima * (1 - PROGRESSION_STEP); // falhou / RPE 10
    else if (rpe == null || rpe <= RPE_READY) base = ultima * (1 + PROGRESSION_STEP); // folgado
    else base = ultima; // RPE 8–9 → mantém

    const e1rm = Number(historicoExercicio.e1rmMax);
    if (e1rm > 0 && Number.isFinite(alvo) && alvo > 0) {
      const cap = cargaFromE1RM(e1rm, alvo); // teto Epley inverso
      if (cap > 0) base = Math.min(base, cap);
    }
    return roundTo(base, ROUND_KG);
  }

  // ── SEM histórico (estimativa por perfil) ─────────────────────────────────
  const peso = Number(perfilUsuario?.pesoCorporal);
  if (Number.isFinite(peso) && peso > 0) {
    const coef = resolveCoef(exercicio);
    const mn = MULT_NIVEL[normNivel(perfilUsuario?.nivel)] ?? MULT_NIVEL.INICIANTE;
    const ms = MULT_SEXO[normSexo(perfilUsuario?.sexo)] ?? MULT_SEXO.M;
    const carga = peso * coef * mn * ms;
    if (carga > 0) return roundTo(carga, ROUND_KG);
  }

  // ── Sem dado utilizável ───────────────────────────────────────────────────
  return null;
}
